/**
 * Canvas State Comparison Utility
 * 
 * Compares the original API data (State A) with the current canvas state (State B)
 * to determine if there are actual changes that should trigger the unsaved changes blocker.
 * 
 * This comparison ignores volatile fields like selection state, timestamps, etc.
 * and focuses only on actual content changes.
 */

/**
 * Normalize a number value for comparison
 */
const normalizeNumber = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num : 0;
};

/**
 * Normalize shape type for consistent comparison
 */
const normalizeShapeType = (type) => {
    if (!type) return null;
    const normalized = String(type).toLowerCase();
    switch (normalized) {
        case "arrow":
            return "arrow";
        case "double_sided_arrow":
        case "double-sided-arrow":
        case "doublesidedarrow":
            return "doubleSidedArrow";
        case "line":
            return "line";
        case "rect":
        case "rectangle":
            return "rectangle";
        case "circle":
            return "circle";
        case "text":
            return "text";
        case "image":
            return "image";
        case "scribble":
            return "scribble";
        default:
            return normalized;
    }
};

/**
 * Extract comparable properties from a shape/element
 * Ignores volatile properties like selection state, IDs (which may be regenerated), etc.
 */
const extractComparableProperties = (element, type) => {
    if (!element) return null;

    const base = {
        type: normalizeShapeType(type || element.type || element.shape_type),
        x: normalizeNumber(element.x),
        y: normalizeNumber(element.y),
        width: normalizeNumber(element.width),
        height: normalizeNumber(element.height),
        rotation: normalizeNumber(element.rotation || 0),
    };

    // Type-specific properties
    switch (base.type) {
        case "image":
            // CRITICAL: For images, ignore downscaling properties (automatic optimizations)
            // Only compare the original image source and dimensions
            return {
                ...base,
                // Compare image source (handle both data URLs and regular URLs)
                // Ignore downscaledSrc as it's an automatic optimization
                src: element.src || element.imgDataUrl || element.originalImageDataUrl || element.image_path || "",
            };

        case "text":
            return {
                ...base,
                text: String(element.text || element.value || element.content || ""),
                fontSize: normalizeNumber(element.fontSize || 16),
                fontFamily: element.fontFamily || "Arial",
                fill: element.fill || element.color || "#000000",
            };

        case "arrow":
        case "doubleSidedArrow":
        case "line":
            return {
                ...base,
                points: Array.isArray(element.points) ? element.points.map(p => normalizeNumber(p)) : [],
                stroke: element.stroke || "#000000",
                strokeWidth: normalizeNumber(element.strokeWidth || 2),
            };

        case "scribble":
            return {
                ...base,
                points: Array.isArray(element.points) ? element.points.map(p => normalizeNumber(p)) : [],
                stroke: element.stroke || "#000000",
                strokeWidth: normalizeNumber(element.strokeWidth || 2),
            };

        case "rectangle":
        case "circle":
            return {
                ...base,
                radius: normalizeNumber(element.radius || 0),
                fillColor: element.fillColor || element.fill || "transparent",
                stroke: element.stroke || "#000000",
                strokeWidth: normalizeNumber(element.strokeWidth || 2),
            };

        default:
            return base;
    }
};

/**
 * Sort elements by their properties for consistent comparison
 * This ensures that element order doesn't affect comparison
 */
const sortElements = (elements) => {
    if (!Array.isArray(elements)) return [];

    return [...elements].sort((a, b) => {
        // Sort by type first
        const typeA = String(a.type || "");
        const typeB = String(b.type || "");
        if (typeA !== typeB) return typeA.localeCompare(typeB);

        // Then by position
        const xDiff = (a.x || 0) - (b.x || 0);
        if (Math.abs(xDiff) > 0.1) return xDiff;

        const yDiff = (a.y || 0) - (b.y || 0);
        if (Math.abs(yDiff) > 0.1) return yDiff;

        // Then by size
        const widthDiff = (a.width || 0) - (b.width || 0);
        if (Math.abs(widthDiff) > 0.1) return widthDiff;

        const heightDiff = (a.height || 0) - (b.height || 0);
        return heightDiff;
    });
};

/**
 * Compare two arrays of elements
 */
const compareElementArrays = (arrayA, arrayB, type) => {
    const elementsA = Array.isArray(arrayA) ? arrayA : [];
    const elementsB = Array.isArray(arrayB) ? arrayB : [];

    // Quick check: different lengths
    if (elementsA.length !== elementsB.length) {
        return false;
    }

    // Extract comparable properties
    const comparableA = elementsA.map(el => extractComparableProperties(el, type));
    const comparableB = elementsB.map(el => extractComparableProperties(el, type));

    // Sort for consistent comparison
    const sortedA = sortElements(comparableA);
    const sortedB = sortElements(comparableB);

    // Deep comparison
    const jsonA = JSON.stringify(sortedA);
    const jsonB = JSON.stringify(sortedB);

    if (jsonA !== jsonB) {
        return false;
    }

    return true;
};

/**
 * Compare a single page's state
 */
const comparePage = (pageA, pageB) => {
    if (!pageA || !pageB) {
        return false;
    }

    // Compare canvas height
    const heightA = normalizeNumber(
        pageA.canvasHeight ||
        pageA.layout_config?.layout?.page_height_vh ||
        100
    );
    const heightB = normalizeNumber(
        pageB.canvasHeight ||
        pageB.layout_config?.layout?.page_height_vh ||
        100
    );

    if (heightA !== heightB) {
        return false;
    }

    // Compare all element types
    const elementTypes = [
        "images",
        "rectangles",
        "circles",
        "arrows",
        "lines",
        "doubleSidedArrows",
        "texts",
        "scribbles",
    ];

    for (const type of elementTypes) {
        const arrayA = pageA[type] || [];
        const arrayB = pageB[type] || [];

        if (!compareElementArrays(arrayA, arrayB, type)) {
            return false;
        }
    }

    return true;
};

/**
 * Main comparison function: Compare State A (API baseline) with State B (current state)
 * 
 * @param {Array} stateA - Original API pages data
 * @param {Array} stateB - Current canvas pages data
 * @returns {boolean} - true if states are equal (no changes), false if there are changes
 */
export const compareCanvasStates = (stateA, stateB) => {
    // Handle null/undefined states
    if (!stateA && !stateB) {
        return true;
    }

    if (!stateA || !stateB) {
        return false;
    }

    // Convert to arrays if needed
    const pagesA = Array.isArray(stateA) ? stateA : [];
    const pagesB = Array.isArray(stateB) ? stateB : [];

    // Compare page counts
    if (pagesA.length !== pagesB.length) {
        return false;
    }

    // If both are empty, they're equal
    if (pagesA.length === 0 && pagesB.length === 0) {
        return true;
    }

    // Compare each page
    // Match pages by canvas_page_id
    for (let i = 0; i < pagesA.length; i++) {
        const pageA = pagesA[i];
        const pageIdA = pageA.canvas_page_id || pageA.id || pageA.pageId;

        // Find matching page in stateB
        const pageB = pagesB.find(p => {
            const pageIdB = p.canvas_page_id || p.id || p.pageId;
            return pageIdB === pageIdA;
        });

        if (!pageB) {
            return false;
        }

        // Compare the pages
        if (!comparePage(pageA, pageB)) {
            return false;
        }
    }

    return true;
};

/**
 * Load current state from IndexedDB for all pages
 * 
 * @param {Array} pages - Array of page objects with canvas_page_id
 * @returns {Promise<Array>} - Array of page states from IndexedDB
 */
export const loadCurrentStateFromIndexedDB = async (pages) => {
    if (!Array.isArray(pages) || pages.length === 0) {
        return [];
    }

    const { loadFromIndexedDB } = require("../../../util/indexedDBUtils");

    const currentStates = await Promise.all(
        pages.map(async (page) => {
            const pageId = page.canvas_page_id || page.id || page.pageId;
            if (!pageId) return null;

            try {
                const result = await loadFromIndexedDB("canvasElements", pageId);
                const data = result?.data || result || {};

                // CRITICAL: Mirror the same unification used when building the API baseline
                // in useCanvasPersistence.js: arrows and doubleSidedArrows are merged into
                // lines so that the two sides of the comparison use the same format.
                // Without this, a canvas with arrows always shows "arrows: N vs arrows: 0"
                // and produces a false-positive "unsaved changes" alert on every page load.
                const rawArrows = data.arrows || [];
                const rawLines = data.lines || [];
                const rawDoubleArrows = data.doubleSidedArrows || [];
                const unifiedLines = [...rawLines, ...rawArrows, ...rawDoubleArrows];

                return {
                    canvas_page_id: pageId,
                    canvasHeight: data.canvasHeight || 100,
                    images: data.images || [],
                    rectangles: data.rectangles || [],
                    circles: data.circles || [],
                    arrows: [],
                    lines: unifiedLines,
                    doubleSidedArrows: [],
                    texts: data.texts || [],
                    scribbles: data.scribbles || [],
                };
            } catch (error) {
                console.error(`Error loading page ${pageId} from IndexedDB:`, error);
                return null;
            }
        })
    );

    return currentStates.filter(Boolean);
};
